import type { ReactNode } from 'react';
import { Topbar } from '@shared/ui/topbar';
import { ArrowLeft, Menu as MenuIcon } from 'lucide-react';
// Deep-imported, not through `@widgets/issue-reader`'s barrel: this
// skeleton is the issue routes' eagerly-imported `pendingComponent`, and
// the barrel would pull the whole reader in with it.
import { IssueCoverPlaceholder } from '@widgets/issue-reader/issue-cover/issue-cover-placeholder';
import { useReaderGoBack } from '@widgets/issue-reader/model/use-reader-go-back';

import styles from './loading.module.css';
import { useT } from '@shared/i18n';

/**
 * The real `Topbar` widget renders here, not a skeleton placeholder for it
 * (user directive, 2026-08-26): navigating from `preview` into
 * `/issues/:issueId/:articleId` used to swap the preview page's real navbar
 * for this page's shimmering fake one while the issue loaded, then swap
 * again for the real navbar once it resolved -- two visible swaps for what
 * should read as one continuous navbar. Its Back button (`ReaderBackButton`
 * below) works with no article/issue data at all, so it's real and
 * functional immediately.
 *
 * `right` also carries a disabled placeholder for the menu button
 * `responsive.tsx`'s real `Topbar` always renders (regardless of which
 * article loads) -- without it, a cold-cache load showed a navbar that then
 * visibly gained an icon the instant the real reader mounted, a second flash
 * on top of the one Phase 0.14 fixed. Reserving the same slot here, at the
 * same size, means only the button's *content* changes (disabled ->
 * functional), not the layout.
 *
 * No sidebar-toggle placeholder and no ToC column any more (2026-09-24):
 * the reader's table of contents is a drawer (`widgets/issue-reader/toc/
 * toc-drawer.tsx`) opened from the floating cover, so the real reader has
 * neither a topbar toggle nor a column for this skeleton to pre-reserve.
 *
 * No player-button placeholder any more (2026-09-14): the audio trigger
 * moved from a topbar icon into the reader's menu (`IssueButton.Audio`),
 * so there's no longer a topbar slot whose appearance/disappearance this
 * skeleton needs to pre-reserve.
 *
 * The floating cover *is* reserved, though (2026-09-24): whenever this
 * navigation carries a `PreviewLink` cover-morph, `IssueCoverPlaceholder`
 * draws the cover at `IssueCover`'s exact spot with the same
 * `view-transition-name`. On a first visit this skeleton, not the reader,
 * is what the transition's "after" snapshot captures -- without it the
 * cover had nothing to morph into and the transition fell back to a
 * cross-fade.
 */
export function Loading() {
	return (
		<div className={styles['loading']}>
			{/* `loading.module.css` positions this via a bare `nav` tag
			    selector (`.loading nav { grid-area: topbar; ... }`), not a
			    class -- no className needed for the grid placement to apply. */}
			<Topbar
				left={<ReaderBackButton />}
				right={
					<PlaceholderButton
						icon={<MenuIcon color="var(--topbar-buttons-stroke)" />}
					/>
				}
			/>
			<ArticleContentSkeleton />
			<div className={styles['cover-layer']}>
				<IssueCoverPlaceholder />
			</div>
		</div>
	);
}

/**
 * A working Back while the issue is still loading -- the reader's own
 * (`responsive.tsx`'s topbar `BackButton`): same markup, same
 * `useReaderGoBack`, so it's in place from the first frame and nothing
 * changes when the reader takes over. Not `@shared/ui/topbar`'s
 * `BackButton`, which renders nothing on the `basic` template -- that left
 * a reader on a slow load with no way out but the browser's own Back, and
 * the arrow popping in once the reader mounted.
 *
 * Leaving here aborts the issue route's loader, which cancels the in-flight
 * issue request with it (`cancelQueryOnAbort`, `@shared/api`).
 */
function ReaderBackButton() {
	const t = useT();
	const readerGoBack = useReaderGoBack();

	return (
		<button
			id="back-button"
			type="button"
			title={t('common.back')}
			className="tooltip down"
			data-text={t('common.back')}
			onClick={readerGoBack}
		>
			<ArrowLeft color="var(--topbar-buttons-stroke)" />
		</button>
	);
}

function PlaceholderButton({ icon }: { icon: ReactNode }) {
	return (
		<button type="button" disabled aria-hidden="true">
			{icon}
		</button>
	);
}

/**
 * The article-text shimmer alone, no topbar chrome around it --
 * exported so other single-article pages with their own real topbar (no
 * sidebar concept at all) can reuse just this piece instead of pulling in
 * `Loading`'s full issue-reader layout. First reuse: `pages/favorite/ui/
 * favorite.tsx`, which already renders its own real `Topbar` for the same
 * reason documented on `Loading` above -- swapping a real navbar for a fake
 * one and back reads as two flashes, not one continuous bar.
 */
export function ArticleContentSkeleton() {
	return (
		<section>
			<div className={styles['content']}>
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
				<div className={styles['shine']} />
			</div>
		</section>
	);
}
